import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Ticket } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
const selectClass = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const emptyForm = {
  code: '', description: '', discount_type: 'percent', discount_value: '',
  min_order_amount: '', max_discount: '', expires_at: '', usage_limit: ''
};

const AdminPromocodesPage = () => {
  const [promos, setPromos] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchPromos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/promocodes`, getAuthHeaders());
      setPromos(res.data || []);
    } catch (e) {
      toast.error('Failed to load promo codes');
    }
  }, []);

  useEffect(() => { fetchPromos(); }, [fetchPromos]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      code: p.code,
      description: p.description || '',
      discount_type: p.discount_type,
      discount_value: String(p.discount_value ?? ''),
      min_order_amount: p.min_order_amount ? String(p.min_order_amount) : '',
      max_discount: p.max_discount ? String(p.max_discount) : '',
      expires_at: p.expires_at || '',
      usage_limit: p.usage_limit ? String(p.usage_limit) : ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      description: form.description,
      discount_type: form.discount_type,
      discount_value: parseFloat(form.discount_value) || 0,
      min_order_amount: parseFloat(form.min_order_amount) || 0,
      max_discount: form.max_discount ? parseFloat(form.max_discount) : null,
      expires_at: form.expires_at || (editing ? '' : null),
      usage_limit: form.usage_limit ? parseInt(form.usage_limit) : null
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/promocodes/${editing.id}`, payload, getAuthHeaders());
        toast.success('Promo code updated');
      } else {
        await axios.post(`${API}/admin/promocodes`, { ...payload, code: form.code }, getAuthHeaders());
        toast.success(`Promo code "${form.code.toUpperCase()}" created`);
      }
      setDialogOpen(false);
      fetchPromos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API}/admin/promocodes/${p.id}`, { active: !p.active }, getAuthHeaders());
      setPromos((prev) => prev.map((x) => (x.id === p.id ? { ...x, active: !x.active } : x)));
      toast.success(!p.active ? `"${p.code}" enabled` : `"${p.code}" disabled`);
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete promo code "${p.code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/promocodes/${p.id}`, getAuthHeaders());
      toast.success('Promo code deleted');
      fetchPromos();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const discountLabel = (p) =>
    p.discount_type === 'percent'
      ? `${p.discount_value}%${p.max_discount ? ` (max ₹${p.max_discount.toLocaleString()})` : ''}`
      : `₹${p.discount_value.toLocaleString()}`;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-slate-900 mb-1 flex items-center gap-2">
              <Ticket className="w-6 h-6 text-[#0047AB]" /> Promo Codes
            </h1>
            <p className="text-sm text-slate-500">Create discount codes customers can apply at checkout.</p>
          </div>
          <Button className="bg-[#0047AB] hover:bg-[#003380]" onClick={openCreate} data-testid="add-promocode-btn">
            <Plus className="w-4 h-4 mr-2" /> New Promo Code
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Code', 'Discount', 'Min Order', 'Expires', 'Usage', 'Status', ''].map((h, i) => (
                  <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 ${i === 6 ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {promos.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-sm text-slate-500" data-testid="no-promocodes">No promo codes yet. Create your first one!</td></tr>
              ) : promos.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50/70" data-testid={`promo-row-${p.code}`}>
                  <td className="px-4 py-3">
                    <span className="font-mono font-bold text-[#0047AB]">{p.code}</span>
                    {p.description && <p className="text-xs text-slate-500 mt-0.5 max-w-[220px] truncate">{p.description}</p>}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-slate-900 whitespace-nowrap">{discountLabel(p)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{p.min_order_amount ? `₹${p.min_order_amount.toLocaleString()}` : '—'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{p.expires_at || 'Never'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{p.used_count || 0}{p.usage_limit ? ` / ${p.usage_limit}` : ''}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleActive(p)}
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${p.active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}
                      data-testid={`promo-toggle-${p.code}`}
                    >
                      {p.active ? 'Active' : 'Disabled'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`promo-edit-${p.code}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(p)} data-testid={`promo-delete-${p.code}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.code}` : 'New Promo Code'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Code *</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. WELCOME10"
                  disabled={!!editing}
                  className="mt-1 font-mono"
                  data-testid="promo-form-code"
                />
              </div>
              <div>
                <Label>Discount Type</Label>
                <select
                  value={form.discount_type}
                  onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                  className={`${selectClass} mt-1`}
                  data-testid="promo-form-type"
                >
                  <option value="percent">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (₹)</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. 10% off for new customers" className="mt-1" data-testid="promo-form-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{form.discount_type === 'percent' ? 'Discount (%)' : 'Discount (₹)'} *</Label>
                <Input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="mt-1" data-testid="promo-form-value" />
              </div>
              {form.discount_type === 'percent' && (
                <div>
                  <Label>Max Discount (₹)</Label>
                  <Input type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} placeholder="No cap" className="mt-1" data-testid="promo-form-maxdiscount" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Min Order (₹)</Label>
                <Input type="number" value={form.min_order_amount} onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })} placeholder="0" className="mt-1" data-testid="promo-form-minorder" />
              </div>
              <div>
                <Label>Expiry Date</Label>
                <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1" data-testid="promo-form-expiry" />
              </div>
              <div>
                <Label>Usage Limit</Label>
                <Input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} placeholder="Unlimited" className="mt-1" data-testid="promo-form-limit" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                className="bg-[#0047AB] hover:bg-[#003380]"
                disabled={saving || !form.discount_value || (!editing && form.code.length < 3)}
                onClick={handleSave}
                data-testid="promo-form-save"
              >
                {saving ? 'Saving…' : editing ? 'Update Code' : 'Create Code'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPromocodesPage;
